import type { PropsWithChildren } from 'react';
import { Children } from 'react';
import AnchoredPopover from '../../generic/AnchoredPopover';
import GrapePopover from '../../sanity/popovers/GrapePopover';
import WineStylePopover from '../../sanity/popovers/WineStylePopover';
import ProductIcon from '../ProductIcon';
import * as style from './ProductCharacteristics.module.css';
import ProductStickers from './ProductStickers';
import type { Sticker } from './ProductTags';

export interface ProductCharacteristicsProps {
    content?: ProductContent;
    stickers?: Sticker[];
    sustainable?: boolean;
}

export interface ProductContent {
    ingredients?: Ingredient[];
    isGoodFor?: IsGoodFor[];
    characteristics?: Characteristic[];
    storagePotential?: StoragePotential;
    style?: Style;
    traits?: Trait[];
}

export interface Ingredient {
    code: string;
    formattedValue: string;
    readableValue: string;
    type?: 'green' | 'blue';
    description?: string;
    name?: string;
}

export interface IsGoodFor {
    code: string;
    name: string;
}

export interface Characteristic {
    name: string;
    readableValue: string;
    value: string;
    description?: string;
}

export interface StoragePotential {
    code: string;
    formattedValue: string;
}

export interface Style {
    code: string;
    description: string;
    name: string;
    image: {
        url: string;
    };
    url: string;
}

export interface Trait {
    formattedValue: string;
    name: string;
    readableValue: string;
}

export default function ProductCharacteristics({ content, stickers, sustainable }: ProductCharacteristicsProps) {
    if (!content) return null;

    return (
        <div className={style.properties}>
            <IsGoodFor {...content} />
            <WineInfo {...content} />
            <ProductStickers stickers={stickers} sustainable={sustainable} />
        </div>
    );
}

export function Traits({ traits }: ProductContent) {
    return (
        !!traits?.length && (
            <Block>
                {traits?.map(({ name, formattedValue, readableValue }) => (
                    <li key={name} className={style.contentItem}>
                        <strong>{name}</strong> <span aria-label={readableValue}>{formattedValue}</span>
                    </li>
                ))}
            </Block>
        )
    );
}

export function IsGoodFor({ isGoodFor, characteristics }: ProductContent) {
    return (
        <div className={style.isGoodFor}>
            {!!isGoodFor?.length && (
                <Block description="Passer til">
                    {isGoodFor?.map(({ name, code }) => (
                        <IconProperty key={name} label={name} icon={`isGoodfor ${code}`} />
                    ))}
                </Block>
            )}
            {!!characteristics?.length && (
                <Block description="Karakteristikk">
                    {characteristics?.map(({ name, value, readableValue, description }) => (
                        <IconPopoverProperty
                            key={name}
                            label={name}
                            icon={`icon-pie-${value}`}
                            aria-label={readableValue}
                            description={description}
                            modalName="Karakteristikk"
                        />
                    ))}
                </Block>
            )}
        </div>
    );
}

export function WineInfo({ ingredients, storagePotential, style: wineStyle }: ProductContent) {
    return (
        <div className={style.wineInfo}>
            {(storagePotential || !!ingredients?.length) && (
                <Block description="Stil, lagring og råstoff">
                    <StyleProperty wineStyle={wineStyle} />
                    {!!storagePotential && (
                        <IconProperty key="storage" icon={`Lagringsgrad ${storagePotential.code}`} label={storagePotential.formattedValue} />
                    )}
                    {!!ingredients?.length && <IngredientsProperty key="ingredients" icon="icon-raastoff" ingredients={ingredients} />}
                </Block>
            )}
        </div>
    );
}

function StyleProperty({ wineStyle }: { wineStyle?: Style }) {
    if (!wineStyle) return null;

    return (
        <li className={style.propertiesItem} aria-label={wineStyle.name}>
            <ProductIcon icon="style" />
            <div className={style.label}>
                <WineStylePopover
                    id={wineStyle.code}
                    name={wineStyle.name}
                    summary={wineStyle.description}
                    image={wineStyle.image?.url}
                    buttonChildren={wineStyle.name}
                    aria-label="Mer informasjon om smak og aroma"
                />
            </div>
        </li>
    );
}

interface IconPropertyProps {
    label: string;
    icon: string;
    'aria-label'?: string;
}

function IconProperty({ icon, label, 'aria-label': ariaLabel }: IconPropertyProps) {
    return (
        <li className={style.propertiesItem} aria-label={ariaLabel}>
            <ProductIcon icon={icon} />
            <div className={style.label}>{label}</div>
        </li>
    );
}

interface IconPopoverPropertyProps extends IconPropertyProps {
    description?: string;
    modalName: string;
}

function IconPopoverProperty({ icon, label, description, modalName, 'aria-label': ariaLabel }: IconPopoverPropertyProps) {
    return (
        <li className={style.propertiesItem} aria-label={ariaLabel}>
            <ProductIcon icon={icon} />
            <div className={style.label}>
                <AnchoredPopover
                    id={`popover-${label}`}
                    className={style.characteristicsPopover}
                    buttonChildren={label}
                    aria-label={`Mer informasjon om ${label}`}
                    data-modal-name={modalName}
                >
                    <h2>
                        {label} <ProductIcon icon={icon} />
                    </h2>
                    <p>{description}</p>
                </AnchoredPopover>
            </div>
        </li>
    );
}

function IngredientsProperty({ icon, ingredients, 'aria-label': ariaLabel }: { ingredients: Ingredient[]; icon: string; 'aria-label'?: string }) {
    return (
        <li className={style.propertiesItem} aria-label={ariaLabel}>
            <ProductIcon icon={icon} />
            <div className={style.label}>
                {ingredients.map((ingredient) => (
                    <IngredientProperty key={ingredient.code} {...ingredient} />
                ))}
            </div>
        </li>
    );
}

function IngredientProperty({ code, name, readableValue, formattedValue, description, type }: Ingredient) {
    if (type) {
        return (
            <div>
                <GrapePopover id={code} name={name ?? ''} summary={description ?? ''} grapeType={type} aria-label={readableValue}>
                    {formattedValue}
                </GrapePopover>
            </div>
        );
    }

    return <div aria-label={readableValue}>{formattedValue}</div>;
}

interface CharacteristicsProps {
    description?: string;
    horizontal?: boolean;
}

function Block({ description, children }: PropsWithChildren<CharacteristicsProps>) {
    if (!Children.toArray(children).length) return null;

    return (
        <div className={style.block}>
            {!!description && <h2 className="sr-only">{description}</h2>}
            <ul className={style.propertiesList}>{children}</ul>
        </div>
    );
}
